import { supabase } from './supabase'
import {
  buildMedicalDocumentDuplicateSignature,
  computeMedicalDocumentFingerprint,
  createSignedMedicalDocumentUrl,
  filterVisibleMedicalDocuments,
  getMedicalDocumentSourcePath,
  removeMedicalDocumentFromPrivateBucket,
  resolveMedicalDocumentUrls,
  uploadMedicalDocumentToPrivateBucket,
  type UploadedMedicalDocumentFile
} from './medical-document-storage'
import type {
  MedicalDocument,
  MedicalDocumentInput,
  DocumentAnalysisResult,
  FileUploadStatus,
  BulkImportProgress,
  BatchProcessingInput,
  BatchProcessing,
  PrescriptionInput,
  AnalyseResultatInput,
  DiagnosticInput,
  Prescription,
  AnalyseResultat,
  Diagnostic,
  DocumentWithExtracts,
  TimelineMedicale
} from '@/types/health'

// Import de l'analyseur Claude
import { analyzeDocumentWithClaude } from './claude-analysis'
import { buildDocumentAnalysisMetadata } from './document-analysis-insights'
import { resolveAnalysisDates } from './medical-document-dates'
import { buildAnalyseResultatRows, syncPatientLabTestCatalog } from './lab-test-catalog'

// ================================
// GESTION DES LOTS DE TRAITEMENT
// ================================

export async function createBatchProcessing(input: BatchProcessingInput): Promise<BatchProcessing> {
  const { data, error } = await supabase
    .from('batch_processing')
    .insert(input)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la création du batch: ${error.message}`)
  }

  return data
}

export async function updateBatchProcessing(
  batchId: string,
  updates: Partial<BatchProcessing>
): Promise<BatchProcessing> {
  const { data, error } = await supabase
    .from('batch_processing')
    .update(updates)
    .eq('id', batchId)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la mise à jour du batch: ${error.message}`)
  }

  return data
}

export async function getBatchProcessing(patientId: string): Promise<BatchProcessing[]> {
  const { data, error } = await supabase
    .from('batch_processing')
    .select('*')
    .eq('patient_id', patientId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Erreur lors de la récupération des batches: ${error.message}`)
  }

  return data || []
}

// ================================
// UPLOAD ET STOCKAGE DES FICHIERS
// ================================

export async function uploadDocumentToStorage(
  file: File,
  patientId: string
): Promise<UploadedMedicalDocumentFile> {
  return uploadMedicalDocumentToPrivateBucket(file, patientId)
}

// ================================
// ANALYSE IA AVEC CLAUDE VIA BEDROCK
// ================================

export async function analyzeDocumentWithAI(
  fileUrl: string,
  fileName: string
): Promise<DocumentAnalysisResult> {
  return await analyzeDocumentWithClaude(fileUrl, fileName)
}

// ================================
// CRÉATION DES DOCUMENTS ET DONNÉES EXTRAITES
// ================================

export async function createDocumentWithAnalysis(
  file: File,
  patientId: string,
  storagePath: string,
  analysisResult: DocumentAnalysisResult
): Promise<MedicalDocument> {
  const sourcePath = getMedicalDocumentSourcePath(file)
  const fileFingerprint = await computeMedicalDocumentFingerprint(file)
  const analysisMetadata = buildDocumentAnalysisMetadata(analysisResult)
  const resolvedDates = resolveAnalysisDates({
    fileName: sourcePath,
    typeDocument: analysisResult.type_document,
    dateDocument: analysisResult.date_document,
    datePrelevement: analysisResult.date_prelevement
  })

  const documentInput: MedicalDocumentInput = {
    patient_id: patientId,
    document_type: analysisResult.type_document,
    title: file.name.replace('.pdf', ''),
    description: analysisResult.resume || null,
    file_name: file.name,
    file_url: storagePath,
    file_size_bytes: file.size,
    mime_type: file.type,
    document_date: resolvedDates.storedDocumentDate,
    practitioner_name: analysisResult.medecin,
    medical_facility: analysisResult.laboratoire,
    specialty: analysisMetadata.inferred_specialty,
    extracted_data: {
      ...analysisResult,
      ...analysisMetadata,
      file_fingerprint_sha256: fileFingerprint,
      duplicate_signature_v1: buildMedicalDocumentDuplicateSignature(file.name, file.size),
      original_file_name: file.name,
      source_relative_path: file.webkitRelativePath || null,
      date_document: resolvedDates.dateDocument,
      date_prelevement: resolvedDates.datePrelevement
    },
    tags: [],
    is_urgent: false,
    is_confidential: false,
    shared_with_doctors: []
  }

  const { data: document, error } = await supabase
    .from('medical_documents')
    .insert(documentInput)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur création document: ${error.message}`)
  }

  // Créer les prescriptions
  if (analysisResult.prescriptions && analysisResult.prescriptions.length > 0) {
    const prescriptions: PrescriptionInput[] = analysisResult.prescriptions.map(p => ({
      document_id: document.id,
      patient_id: patientId,
      medicament: p.medicament,
      dosage: p.dosage || null,
      frequence: p.frequence || null,
      duree: p.duree || null,
      date_prescription: resolvedDates.dateDocument,
      medecin: analysisResult.medecin,
      instructions: p.instructions || null,
      statut: 'actif'
    }))

    const { error: prescError } = await supabase
      .from('prescriptions')
      .insert(prescriptions)

    if (prescError) {
      console.error('Erreur création prescriptions:', prescError)
    }
  }

  // Créer les analyses
  if (analysisResult.analyses && analysisResult.analyses.length > 0) {
    const analysisDate = resolvedDates.datePrelevement || resolvedDates.dateDocument
    const analyses: AnalyseResultatInput[] = buildAnalyseResultatRows({
      documentId: document.id,
      patientId,
      analysisDate,
      analysisResult
    })

    const { error: analysesError } = await supabase
      .from('analyses_resultats')
      .insert(analyses)

    if (analysesError) {
      console.error('Erreur création analyses:', analysesError)
    } else {
      await syncPatientLabTestCatalog({
        documentId: document.id,
        patientId,
        analysisDate,
        analysisResult
      })
    }
  }

  // Créer les diagnostics
  if (analysisResult.diagnostics && analysisResult.diagnostics.length > 0) {
    const diagnostics: DiagnosticInput[] = analysisResult.diagnostics.map(d => ({
      document_id: document.id,
      patient_id: patientId,
      diagnostic: d.diagnostic,
      type_diagnostic: d.type_diagnostic || 'principal',
      code_cim10: null,
      medecin: analysisResult.medecin,
      specialite: null,
      date_diagnostic: resolvedDates.dateDocument,
      gravite: d.gravite || null,
      commentaire: null
    }))

    const { error: diagError } = await supabase
      .from('diagnostics')
      .insert(diagnostics)

    if (diagError) {
      console.error('Erreur création diagnostics:', diagError)
    }
  }

  return document
}

// ================================
// PROCESSUS D'IMPORT EN MASSE
// ================================

export async function processBulkDocumentImport(
  files: File[],
  patientId: string,
  onProgress: (progress: BulkImportProgress) => void
): Promise<BulkImportProgress> {

  const batchName = `Import ${files.length} fichiers - ${new Date().toLocaleString()}`

  // Créer le batch de traitement
  const batch = await createBatchProcessing({
    patient_id: patientId,
    batch_name: batchName,
    total_files: files.length
  })

  const progress: BulkImportProgress = {
    total_files: files.length,
    processed_files: 0,
    success_files: 0,
    error_files: 0,
    status: 'preparing',
    files_status: files.map(file => ({
      file,
      status: 'pending',
      progress: 0
    }))
  }

  onProgress(progress)

  progress.status = 'uploading'
  onProgress(progress)

  // Traiter les fichiers un par un pour éviter la surcharge
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    const sourcePath = getMedicalDocumentSourcePath(file)
    const fileStatus = progress.files_status[i]
    let uploadedFile: UploadedMedicalDocumentFile | null = null

    try {
      progress.current_file = sourcePath
      fileStatus.status = 'uploading'
      fileStatus.progress = 0
      onProgress(progress)

      // 1. Upload du fichier
      uploadedFile = await uploadDocumentToStorage(file, patientId)
      fileStatus.progress = 33
      onProgress(progress)

      // 2. Analyse IA
      fileStatus.status = 'analyzing'
      fileStatus.progress = 50
      onProgress(progress)

      const analysisUrl = uploadedFile.signedUrl || await createSignedMedicalDocumentUrl(uploadedFile.storagePath)
      const analysisResult = await analyzeDocumentWithAI(analysisUrl, sourcePath)
      fileStatus.analysis_result = analysisResult
      fileStatus.progress = 75
      onProgress(progress)

      // 3. Création du document avec données extraites
      const document = await createDocumentWithAnalysis(file, patientId, uploadedFile.storagePath, analysisResult)

      fileStatus.status = 'success'
      fileStatus.progress = 100
      fileStatus.document_id = document.id
      progress.success_files++

    } catch (error: any) {
      console.error(`❌ Erreur traitement ${file.name}:`, error)

      if (uploadedFile?.storagePath) {
        await removeMedicalDocumentFromPrivateBucket(uploadedFile.storagePath)
      }

      fileStatus.status = 'error'
      fileStatus.error = error.message
      fileStatus.progress = 0
      progress.error_files++
    }

    progress.processed_files++
    onProgress(progress)

    // Petite pause entre les fichiers pour ne pas surcharger
    if (i < files.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 500))
    }
  }

  // Finaliser le batch
  progress.status = progress.error_files > 0 ? 'completed' : 'completed'
  progress.current_file = undefined

  await updateBatchProcessing(batch.id, {
    processed_files: progress.processed_files,
    success_files: progress.success_files,
    error_files: progress.error_files,
    status: progress.status,
    end_time: new Date().toISOString(),
    error_log: progress.files_status
      .filter(f => f.status === 'error')
      .map(f => ({
        file: f.file.name,
        error: f.error || 'Erreur inconnue',
        timestamp: new Date().toISOString()
      }))
  })

  onProgress(progress)
  return progress
}

// ================================
// CONSULTATION DES DONNÉES EXTRAITES
// ================================

export async function getDocumentsWithExtracts(patientId: string): Promise<DocumentWithExtracts[]> {
  const { data, error } = await supabase
    .from('documents_with_extracts')
    .select('*')
    .eq('patient_id', patientId)
    .order('document_date', { ascending: false })

  if (error) {
    throw new Error(`Erreur récupération documents: ${error.message}`)
  }

  return resolveMedicalDocumentUrls(filterVisibleMedicalDocuments(data || []))
}

export async function getPrescriptions(patientId: string): Promise<Prescription[]> {
  const { data, error } = await supabase
    .from('prescriptions')
    .select('*')
    .eq('patient_id', patientId)
    .order('date_prescription', { ascending: false })

  if (error) {
    throw new Error(`Erreur récupération prescriptions: ${error.message}`)
  }

  return data || []
}

export async function getAnalysesResultats(patientId: string): Promise<AnalyseResultat[]> {
  const { data, error } = await supabase
    .from('analyses_resultats')
    .select('*')
    .eq('patient_id', patientId)
    .order('date_analyse', { ascending: false })

  if (error) {
    throw new Error(`Erreur récupération analyses: ${error.message}`)
  }

  return data || []
}

export async function getDiagnostics(patientId: string): Promise<Diagnostic[]> {
  const { data, error } = await supabase
    .from('diagnostics')
    .select('*')
    .eq('patient_id', patientId)
    .order('date_diagnostic', { ascending: false })

  if (error) {
    throw new Error(`Erreur récupération diagnostics: ${error.message}`)
  }

  return data || []
}

export async function getTimelineMedicale(patientId: string): Promise<TimelineMedicale[]> {
  const { data, error } = await supabase
    .from('timeline_medicale')
    .select('*')
    .eq('patient_id', patientId)
    .order('date_event', { ascending: false })
    .limit(100)

  if (error) {
    throw new Error(`Erreur récupération timeline: ${error.message}`)
  }

  return resolveMedicalDocumentUrls(filterVisibleMedicalDocuments(data || []))
}

// ================================
// RECHERCHE ET FILTRES
// ================================

export async function searchInMedicalData(
  patientId: string,
  searchTerm: string
): Promise<{
  documents: DocumentWithExtracts[]
  prescriptions: Prescription[]
  analyses: AnalyseResultat[]
  diagnostics: Diagnostic[]
}> {
  const term = `%${searchTerm.toLowerCase()}%`

  const [documentsResult, prescriptionsResult, analysesResult, diagnosticsResult] = await Promise.allSettled([
    supabase
      .from('documents_with_extracts')
      .select('*')
      .eq('patient_id', patientId)
      .or(`title.ilike.${term},description.ilike.${term},practitioner_name.ilike.${term}`)
      .order('document_date', { ascending: false }),

    supabase
      .from('prescriptions')
      .select('*')
      .eq('patient_id', patientId)
      .or(`medicament.ilike.${term},instructions.ilike.${term},medecin.ilike.${term}`)
      .order('date_prescription', { ascending: false }),

    supabase
      .from('analyses_resultats')
      .select('*')
      .eq('patient_id', patientId)
      .or(`nom_analyse.ilike.${term},valeur.ilike.${term},laboratoire.ilike.${term}`)
      .order('date_analyse', { ascending: false }),

    supabase
      .from('diagnostics')
      .select('*')
      .eq('patient_id', patientId)
      .or(`diagnostic.ilike.${term},medecin.ilike.${term},commentaire.ilike.${term}`)
      .order('date_diagnostic', { ascending: false })
  ])

  return {
    documents:
      documentsResult.status === 'fulfilled'
        ? filterVisibleMedicalDocuments(documentsResult.value.data || [])
        : [],
    prescriptions: prescriptionsResult.status === 'fulfilled' ? (prescriptionsResult.value.data || []) : [],
    analyses: analysesResult.status === 'fulfilled' ? (analysesResult.value.data || []) : [],
    diagnostics: diagnosticsResult.status === 'fulfilled' ? (diagnosticsResult.value.data || []) : []
  }
}
